import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminDeleteTerm,
  adminListTaxonomy,
  adminUpsertTerm,
} from "@/lib/admin-blog.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/admin/taxonomy")({
  component: TaxonomyPage,
});

type Kind = "author" | "category" | "tag";

function TaxonomyPage() {
  const list = useServerFn(adminListTaxonomy);
  const upsert = useServerFn(adminUpsertTerm);
  const del = useServerFn(adminDeleteTerm);
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ["adminTaxonomy"], queryFn: () => list() });

  const upMut = useMutation({
    mutationFn: (input: any) => upsert({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminTaxonomy"] }),
  });
  const delMut = useMutation({
    mutationFn: (input: { kind: Kind; id: string }) => del({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminTaxonomy"] }),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-foreground">Authors & taxonomy</h1>

      <Tabs defaultValue="authors">
        <TabsList>
          <TabsTrigger value="authors">Authors</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
        </TabsList>

        <TabsContent value="authors">
          <AuthorList items={q.data?.authors ?? []} onSave={upMut.mutate} onDelete={(id) => delMut.mutate({ kind: "author", id })} />
        </TabsContent>
        <TabsContent value="categories">
          <SimpleList kind="category" items={q.data?.categories ?? []} onSave={upMut.mutate} onDelete={(id) => delMut.mutate({ kind: "category", id })} />
        </TabsContent>
        <TabsContent value="tags">
          <SimpleList kind="tag" items={q.data?.tags ?? []} onSave={upMut.mutate} onDelete={(id) => delMut.mutate({ kind: "tag", id })} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleList({
  kind, items, onSave, onDelete,
}: {
  kind: "category" | "tag";
  items: any[];
  onSave: (input: any) => void;
  onDelete: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; onSave({ kind, id: null, name, description: kind === "category" ? desc : undefined }); setName(""); setDesc(""); }}
        className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3"
      >
        <Input placeholder="New name…" value={name} onChange={(e) => setName(e.target.value)} className="w-60" />
        {kind === "category" && (
          <Input placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} className="grow" />
        )}
        <Button type="submit">Add</Button>
      </form>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Slug</th><th /></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <Row key={it.id} item={it} kind={kind} onSave={onSave} onDelete={onDelete} />
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">None yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({
  item, kind, onSave, onDelete,
}: {
  item: any;
  kind: Kind;
  onSave: (input: any) => void;
  onDelete: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [slug, setSlug] = useState(item.slug);
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2">
        {editing ? <Input value={name} onChange={(e) => setName(e.target.value)} /> : item.name}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {editing ? <Input value={slug} onChange={(e) => setSlug(e.target.value)} /> : item.slug}
      </td>
      <td className="px-3 py-2 text-right">
        {editing ? (
          <>
            <Button size="sm" onClick={() => { onSave({ kind, id: item.id, name, slug }); setEditing(false); }}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
            <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete "${item.name}"?`)) onDelete(item.id); }}>Delete</Button>
          </>
        )}
      </td>
    </tr>
  );
}

function AuthorList({
  items, onSave, onDelete,
}: {
  items: any[];
  onSave: (input: any) => void;
  onDelete: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [avatar, setAvatar] = useState("");
  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; onSave({ kind: "author", id: null, name, bio: bio || undefined, avatar_url: avatar || undefined }); setName(""); setBio(""); setAvatar(""); }}
        className="grid gap-2 rounded-md border border-border bg-card p-3 md:grid-cols-3"
      >
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder="Avatar URL (optional)" value={avatar} onChange={(e) => setAvatar(e.target.value)} />
        <Textarea placeholder="Bio (optional)" value={bio} onChange={(e) => setBio(e.target.value)} className="md:col-span-3" rows={2} />
        <div className="md:col-span-3"><Button type="submit">Add author</Button></div>
      </form>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Slug</th><th /></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <Row key={it.id} item={it} kind="author" onSave={onSave} onDelete={onDelete} />
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">None yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}